import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import posthog from 'posthog-js'
import {
  onGetUsersAsync,
  onSaveUserPensionConfigAsync,
  onSetUserRoleAsync,
  onUpdateUserStatusAsync,
} from './PensionUser.slice'
import {
  getPensionCalculationsByUserId,
  getRolesCatalog,
} from '../../services/pension'
import { getUserMenus } from '../../modules/Auth/providers/services/authSupabase'
import {
  UserAccountMeta,
  UserAuditLog,
  UserConfigHistory,
  UserPensionConfig,
  UserSessions,
  UserSupportContext,
} from './PensionUserOps'
import { getUserOpsAccount } from '../../services/userOps'
import {
  getAvatarSrc,
  handleAvatarError,
} from '../../config/settings/avatar'
import routes from '../../config/settings/routes'
import { useAuthProvider } from '../../modules/Auth/providers/AuthSupabaseProvider/AuthProvider.hook'
import { useOnlineUserIds } from './useOnlineUserIds'
import Spinner from '../../components/Spinners/Spinner'
import Switch from '../../components/Basics/Switch'
import * as S from './PensionUser.styles'

const FILTERS = [
  { key: 'all', label: 'Todos' },
  { key: 'online', label: 'En línea' },
  { key: 'active', label: 'Activos' },
  { key: 'inactive', label: 'Inactivos' },
]

const isActive = (status) => status === 'active'
const isPaid = (status) => status === 'payed'

const ROLE_LABELS = {
  admin: 'Admin',
  user: 'Usuario',
}

const ROLE_HINTS = {
  admin: 'Usuarios, UTM y Bitácora',
  user: 'Inicio, Pensiones y Preferencias',
}

const setupLabel = (user) => {
  if (user.setupCompleted || user.onboardingStatus === 'completed') {
    return { label: 'Setup completo', done: true }
  }
  if (user.onboardingStatus === 'skipped') {
    return { label: 'Setup omitido', done: false }
  }
  return { label: 'Setup pendiente', done: false }
}

const AvatarImage = ({ user, size }) => (
  <S.Avatar
    $size={size}
    src={getAvatarSrc(user.avatarUrl)}
    alt={user.name || user.email || 'Usuario'}
    referrerPolicy="no-referrer"
    onError={handleAvatarError}
  />
)

const UserCard = ({ user, online, onOpen }) => {
  const setup = setupLabel(user)

  return (
    <S.UserCard
      type="button"
      onClick={() => onOpen(user)}
      aria-label={`Ver detalle de ${user.name || user.email}`}
    >
      <S.CardMain>
        <AvatarImage user={user} size={40} />

        <S.UserInfo>
          <S.IdentityRow>
            <S.UserName>{user.name || 'Sin nombre'}</S.UserName>
            {online && <S.StatusPill $live>En línea</S.StatusPill>}
            <S.StatusPill>
              {isActive(user.status) ? 'Activo' : 'Inactivo'}
            </S.StatusPill>
            <S.StatusPill $warn={!setup.done}>{setup.label}</S.StatusPill>
          </S.IdentityRow>
          <S.UserEmail>{user.email}</S.UserEmail>
        </S.UserInfo>
      </S.CardMain>
    </S.UserCard>
  )
}

const UserPensions = ({ userId }) => {
  const [pensions, setPensions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const data = await getPensionCalculationsByUserId(userId)
        if (!cancelled) setPensions(data)
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'No pudimos cargar las pensiones.')
          setPensions([])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [userId])

  const pensionsByYear = useMemo(() => {
    const groups = []
    let currentYear = null
    let currentGroup = null

    pensions.forEach((pension) => {
      const raw = pension.month || pension.calculationDateRaw || ''
      const year = raw ? String(raw).slice(0, 4) : 'Sin año'
      if (year !== currentYear) {
        currentYear = year
        currentGroup = { year, items: [] }
        groups.push(currentGroup)
      }
      currentGroup.items.push(pension)
    })

    return groups
  }, [pensions])

  return (
    <S.DetailSection>
      <S.SectionHeader>
        <S.SectionTitle>Registros de pensión</S.SectionTitle>
        {!loading && !error && (
          <S.SectionHint>
            {pensions.length} registro{pensions.length === 1 ? '' : 's'}
          </S.SectionHint>
        )}
      </S.SectionHeader>

      {loading && <Spinner />}

      {!loading && error && <S.ErrorText>{error}</S.ErrorText>}

      {!loading && !error && pensions.length === 0 && (
        <S.EmptyState>
          <strong>Sin pensiones registradas</strong>
          <span>Este usuario aún no tiene cálculos de pensión.</span>
        </S.EmptyState>
      )}

      {!loading && !error && pensions.length > 0 && (
        <S.PensionYears>
          {pensionsByYear.map((group) => (
            <S.PensionYearGroup key={group.year}>
              <S.PensionYearLabel>{group.year}</S.PensionYearLabel>
              <S.PensionList>
                {group.items.map((pension) => {
                  const paid = isPaid(pension.status)
                  return (
                    <S.PensionRow key={pension.id}>
                      <S.PensionMain>
                        <S.PensionAmount>
                          {pension.calculatedAmount}
                        </S.PensionAmount>
                        <S.PensionMeta>
                          {pension.targetMonth || 'Sin período'}
                          {pension.calculationDate
                            ? ` · ${pension.calculationDate}`
                            : ''}
                          {pension.paymentAt ? ` · pagado ${pension.paymentAt}` : ''}
                        </S.PensionMeta>
                        {pension.formula && (
                          <S.FormulaText>{pension.formula}</S.FormulaText>
                        )}
                      </S.PensionMain>
                      <S.PensionSide>
                        <S.MetaValue
                          $muted={
                            !(pension.referenceLabel || pension.utmValue)
                          }
                        >
                          {pension.referenceLabel ||
                            `UTM ${pension.utmValue || '—'}`}
                        </S.MetaValue>
                        <S.PaymentBadge $paid={paid}>
                          {paid ? 'Pagado' : 'Pendiente'}
                        </S.PaymentBadge>
                      </S.PensionSide>
                    </S.PensionRow>
                  )
                })}
              </S.PensionList>
            </S.PensionYearGroup>
          ))}
        </S.PensionYears>
      )}
    </S.DetailSection>
  )
}

const UserAccess = ({ user, currentUserId, onToggleRole }) => {
  const [catalog, setCatalog] = useState([])
  const [menus, setMenus] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [savingRoleId, setSavingRoleId] = useState(null)

  const activeRoles = user.roles || []
  const isSelf = user.id === currentUserId

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const [roles, userMenus] = await Promise.all([
          getRolesCatalog(),
          getUserMenus(user.id),
        ])
        if (!cancelled) {
          setCatalog(roles)
          setMenus(userMenus)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'No pudimos cargar el acceso.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [user.id])

  const handleToggleRole = async (role) => {
    const nextActive = !activeRoles.includes(role.name)
    if (isSelf && role.name === 'admin' && !nextActive) return

    setSavingRoleId(role.id)
    try {
      await onToggleRole({
        userId: user.id,
        roleId: role.id,
        roleName: role.name,
        active: nextActive,
      })
      const userMenus = await getUserMenus(user.id)
      setMenus(userMenus)
    } catch (err) {
      setError(err?.message || 'No pudimos actualizar el rol.')
    } finally {
      setSavingRoleId(null)
    }
  }

  return (
    <S.DetailSection>
      <S.SectionHeader>
        <S.SectionTitle>Acceso</S.SectionTitle>
        {!loading && !error && (
          <S.SectionHint>
            {activeRoles.length} rol{activeRoles.length === 1 ? '' : 'es'}
          </S.SectionHint>
        )}
      </S.SectionHeader>

      {loading && <Spinner />}

      {!loading && error && <S.ErrorText>{error}</S.ErrorText>}

      {!loading && catalog.length > 0 && (
        <>
          {activeRoles.length === 0 && (
            <S.WarningText>
              Sin roles: no verá el menú. Activa al menos el rol Usuario.
            </S.WarningText>
          )}

          <S.RoleList>
            {catalog.map((role) => {
              const assigned = activeRoles.includes(role.name)
              const lockAdmin =
                isSelf && role.name === 'admin' && assigned
              return (
                <S.RoleRow key={role.id}>
                  <S.RoleInfo>
                    <S.RoleName>
                      {ROLE_LABELS[role.name] || role.name}
                    </S.RoleName>
                    <S.RoleHint>
                      {lockAdmin
                        ? 'No puedes quitarte el rol admin'
                        : ROLE_HINTS[role.name] || role.description || ''}
                    </S.RoleHint>
                  </S.RoleInfo>
                  <Switch
                    checked={assigned}
                    disabled={lockAdmin || savingRoleId === role.id}
                    onChange={() => handleToggleRole(role)}
                    ariaLabel={`${assigned ? 'Quitar' : 'Asignar'} rol ${
                      ROLE_LABELS[role.name] || role.name
                    }`}
                  />
                </S.RoleRow>
              )
            })}
          </S.RoleList>

          <S.MetaItem>
            <S.MetaLabel>Menús efectivos</S.MetaLabel>
            {menus.length === 0 ? (
              <S.MetaValue $muted>Ninguno — sin menú en el sidebar</S.MetaValue>
            ) : (
              <S.MenuChips>
                {menus.map((menu) => (
                  <S.MenuChip key={menu.id}>{menu.name}</S.MenuChip>
                ))}
              </S.MenuChips>
            )}
          </S.MetaItem>
        </>
      )}
    </S.DetailSection>
  )
}

const UserDetail = ({
  user,
  online,
  currentUserId,
  error,
  onBack,
  onToggleStatus,
  onToggleRole,
  onSavePensionConfig,
}) => {
  const setup = setupLabel(user)
  const [account, setAccount] = useState(null)
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0)
  const isSelf = user.id === currentUserId
  const lockDeactivate = isSelf && isActive(user.status)

  useEffect(() => {
    let cancelled = false
    getUserOpsAccount(user.id)
      .then((data) => {
        if (!cancelled) setAccount(data)
      })
      .catch(() => {
        if (!cancelled) setAccount(null)
      })
    return () => {
      cancelled = true
    }
  }, [user.id])

  const handleSavePensionConfig = async (payload) => {
    await onSavePensionConfig(payload)
    setHistoryRefreshKey((key) => key + 1)
  }

  return (
    <S.DetailView>
      <S.DetailToolbar>
        <S.BackButton type="button" onClick={onBack}>
          ← Volver a usuarios
        </S.BackButton>
      </S.DetailToolbar>

      <S.DetailCard>
        <S.DetailHero>
          <AvatarImage user={user} size={48} />
          <S.UserInfo>
            <S.IdentityRow>
              <S.UserName>{user.name || 'Sin nombre'}</S.UserName>
              {online && <S.StatusPill $live>En línea</S.StatusPill>}
              <S.StatusPill>
                {isActive(user.status) ? 'Activo' : 'Inactivo'}
              </S.StatusPill>
              <S.StatusPill $warn={!setup.done}>{setup.label}</S.StatusPill>
            </S.IdentityRow>
          </S.UserInfo>
          <S.StatusToggle
            title={
              lockDeactivate
                ? 'No puedes desactivar tu propia cuenta'
                : undefined
            }
          >
            <span>Activo</span>
            <Switch
              checked={isActive(user.status)}
              disabled={lockDeactivate}
              onChange={() => onToggleStatus(user)}
              ariaLabel={`Marcar usuario ${user.name || user.email} como ${
                isActive(user.status) ? 'inactivo' : 'activo'
              }`}
            />
          </S.StatusToggle>
        </S.DetailHero>

        {lockDeactivate && (
          <S.SectionHint>No puedes desactivar tu propia cuenta.</S.SectionHint>
        )}
        {error && <S.ErrorText>{error}</S.ErrorText>}

        <UserAccountMeta user={user} account={account} />
        <UserPensionConfig
          user={user}
          onSave={handleSavePensionConfig}
        />
        <UserSessions userId={user.id} />

        <UserAccess
          user={user}
          currentUserId={currentUserId}
          onToggleRole={onToggleRole}
        />

        <UserConfigHistory userId={user.id} refreshKey={historyRefreshKey} />
        <UserPensions userId={user.id} />
        <UserSupportContext userId={user.id} userName={user.name} />
        <UserAuditLog userId={user.id} />
      </S.DetailCard>
    </S.DetailView>
  )
}

const PensionUserList = () => {
  const { error, loading, users = [] } = useSelector(
    (state) => state.adminPensionUsers
  )
  const { user: authUser } = useAuthProvider()
  const onlineIds = useOnlineUserIds()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { userId } = useParams()
  const [filter, setFilter] = useState('all')

  const counts = useMemo(() => {
    const active = users.filter((user) => isActive(user.status)).length
    const inactive = users.filter((user) => !isActive(user.status)).length
    const online = users.filter((user) => onlineIds.has(user.id)).length
    return {
      all: users.length,
      online,
      active,
      inactive,
    }
  }, [users, onlineIds])

  const filteredUsers = useMemo(() => {
    if (filter === 'all') return users
    if (filter === 'online') {
      return users.filter((user) => onlineIds.has(user.id))
    }
    return users.filter((user) => user.status === filter)
  }, [users, filter, onlineIds])

  const selectedUser = useMemo(
    () => (userId ? users.find((user) => user.id === userId) || null : null),
    [users, userId]
  )

  const handleToggleStatus = (row) => {
    const newStatus = isActive(row.status) ? 'inactive' : 'active'
    if (row.id === authUser?.id && newStatus === 'inactive') return
    posthog.capture('user_status_updated', {
      new_status: newStatus,
      target_user_id: row.id,
    })
    dispatch(onUpdateUserStatusAsync({ id: row.id, status: newStatus }))
  }

  const handleToggleRole = ({ userId: id, roleId, roleName, active }) => {
    posthog.capture('user_role_updated', {
      role: roleName,
      active,
      target_user_id: id,
    })
    return dispatch(
      onSetUserRoleAsync({ userId: id, roleId, roleName, active })
    ).unwrap()
  }

  const handleSavePensionConfig = ({ method, amount, percentage, paymentDay }) => {
    posthog.capture('user_pension_config_updated', {
      method,
      target_user_id: selectedUser?.id,
    })
    return dispatch(
      onSaveUserPensionConfigAsync({
        userId: selectedUser.id,
        method,
        amount,
        percentage,
        paymentDay,
      })
    ).unwrap()
  }

  useEffect(() => {
    dispatch(onGetUsersAsync())
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (userId) {
    if (selectedUser) {
      return (
        <S.Container>
          <UserDetail
            user={selectedUser}
            online={onlineIds.has(selectedUser.id)}
            currentUserId={authUser?.id}
            error={error}
            onBack={() => navigate(routes.pensionUsers)}
            onToggleStatus={handleToggleStatus}
            onToggleRole={handleToggleRole}
            onSavePensionConfig={handleSavePensionConfig}
          />
        </S.Container>
      )
    }

    if (loading) {
      return (
        <S.Container>
          <Spinner />
        </S.Container>
      )
    }

    if (error) {
      return (
        <S.Container>
          <S.ErrorText>Error al cargar los usuarios.</S.ErrorText>
        </S.Container>
      )
    }

    return (
      <S.Container>
        <S.DetailView>
          <S.DetailToolbar>
            <S.BackButton
              type="button"
              onClick={() => navigate(routes.pensionUsers)}
            >
              ← Volver a usuarios
            </S.BackButton>
          </S.DetailToolbar>
          <S.EmptyState>
            <strong>Usuario no encontrado</strong>
            <span>Ese perfil no existe o ya no está disponible.</span>
          </S.EmptyState>
        </S.DetailView>
      </S.Container>
    )
  }

  return (
    <S.Container>
      {loading && <Spinner />}
      {!loading && error && (
        <S.ErrorText>Error al cargar los usuarios.</S.ErrorText>
      )}

      {!loading && !error && users.length === 0 && (
        <S.EmptyState>
          <strong>Sin usuarios</strong>
          <span>Cuando existan perfiles, aparecerán aquí.</span>
        </S.EmptyState>
      )}

      {!loading && !error && users.length > 0 && (
        <>
          <S.Filters>
            {FILTERS.map(({ key, label }) => (
              <S.FilterChip
                key={key}
                type="button"
                $active={filter === key}
                onClick={() => setFilter(key)}
              >
                {label}
                <S.FilterCount>({counts[key]})</S.FilterCount>
              </S.FilterChip>
            ))}
          </S.Filters>

          {filteredUsers.length === 0 ? (
            <S.EmptyState>
              <strong>
                {filter === 'online'
                  ? 'Nadie está en línea'
                  : 'Sin resultados para este filtro'}
              </strong>
              <span>
                {filter === 'online'
                  ? 'En línea significa que la app está viva ahora (heartbeat). Una sesión JWT puede seguir abierta con el teléfono bloqueado.'
                  : 'Prueba con otro estado de usuario.'}
              </span>
            </S.EmptyState>
          ) : (
            <S.Section>
              <S.SectionHeader>
                <S.SectionTitle>Usuarios</S.SectionTitle>
                <S.SectionHint>
                  {filteredUsers.length} registro
                  {filteredUsers.length === 1 ? '' : 's'}
                </S.SectionHint>
              </S.SectionHeader>
              <S.CardsWrapper>
                {filteredUsers.map((user) => (
                  <UserCard
                    key={user.id}
                    user={user}
                    online={onlineIds.has(user.id)}
                    onOpen={(selected) =>
                      navigate(`${routes.pensionUsers}/${selected.id}`)
                    }
                  />
                ))}
              </S.CardsWrapper>
            </S.Section>
          )}
        </>
      )}
    </S.Container>
  )
}

export default PensionUserList
